export const TURTLE_KEYS = ['inputs', 'outputs', 'responsibilities', 'resources', 'methods', 'kpis'] as const
export type TurtleKey = typeof TURTLE_KEYS[number]

export interface TurtleProcessLink {
  process_id: number
  release_id?: number
  release_number?: number
  code?: string
  title?: string
  category?: string
}

export interface TurtleEntry {
  text: string
  process_ids: number[]
  linked_processes?: TurtleProcessLink[]
}

export type TurtleData = Record<TurtleKey, TurtleEntry[]>

export function normalizeTurtleData(value: any): TurtleData {
  return Object.fromEntries(TURTLE_KEYS.map((key) => {
    const entries = Array.isArray(value?.[key]) ? value[key] : []
    return [key, entries.map((entry: any) => {
      if (typeof entry === 'string') return { text: entry, process_ids: [] }
      return {
        text: String(entry?.text || ''),
        process_ids: [...new Set((Array.isArray(entry?.process_ids) ? entry.process_ids : []).map(Number).filter((id: number) => id > 0))],
        ...(Array.isArray(entry?.linked_processes) ? { linked_processes: entry.linked_processes } : {}),
      }
    })]
  })) as TurtleData
}

export function serializeTurtleData(value: any): TurtleData {
  const normalized = normalizeTurtleData(value)
  return Object.fromEntries(TURTLE_KEYS.map((key) => [key, normalized[key]
    .map((entry) => ({ text: entry.text.trim(), process_ids: [...new Set(entry.process_ids)] }))
    .filter((entry) => entry.text.length > 0)])) as TurtleData
}

export function updateTurtleEntry(value: TurtleData, key: TurtleKey, index: number, patch: Partial<TurtleEntry>): TurtleData {
  const copy = normalizeTurtleData(value)
  copy[key][index] = { ...copy[key][index], ...patch }
  return copy
}

export function addTurtleEntry(value: TurtleData, key: TurtleKey): TurtleData {
  const copy = normalizeTurtleData(value)
  copy[key].push({ text: '', process_ids: [] })
  return copy
}

export function removeTurtleEntry(value: TurtleData, key: TurtleKey, index: number): TurtleData {
  const copy = normalizeTurtleData(value)
  copy[key].splice(index, 1)
  return copy
}

export function toggleTurtleProcess(value: TurtleData, key: TurtleKey, index: number, processId: number): TurtleData {
  const entry = normalizeTurtleData(value)[key][index]
  const ids = entry.process_ids.includes(processId)
    ? entry.process_ids.filter((id) => id !== processId)
    : [...entry.process_ids, processId]
  return updateTurtleEntry(value, key, index, { process_ids: ids, linked_processes: undefined })
}
